/**
 * D-1 出行人 —— 契约 §7 D-1 / identity 的 `TravelerDtos.java`。
 *
 * ⚠ 形状以 `TravelerDtos.java` 为准（字段名与 `identity/v1/traveler.proto` 一一对应）。
 *
 * ★★ 两条贯穿全模块的纪律，做界面前必须先认下：
 *
 *  1. **出参一个明文字段都没有**：证件号只回 `docNumberMasked`、手机只回 `phoneMasked`，
 *     而 `gender` / `nationality` / `birthday` **连脱敏值都不回**（契约有意少暴露）。
 *     所以前端**没有能力**回传一个「完整对象」—— 这直接决定了编辑语义（见下）。
 *  2. **编辑（PUT）时「留空 = 保持不变」**，这跟大多数表单「整个对象提交回去」的习惯是**反的**。
 *     整包回传的两种写法都会造成脏数据，且都不报错：
 *       - 留空 → 服务端把没填的覆写成 null（改个姓名把手机号弄丢）；
 *       - 回传掩码 → 把 `139****1234` 当明文加密落库，`phone_masked` 看着完全正常，
 *         要等下单被供应商拒才发现。后端已加 `rejectMaskedEcho` 主动拦（400），但**拦住不等于可以犯**。
 */
import { z } from 'zod'

/**
 * 脱敏摘要（`TravelerSummaryView`）。**永不含明文**。
 *
 * ⚠ 只有 `travelerId` 必需；其余按 looseObject 容错 —— proto3 的 string 取不到是空串，
 *   后端 `blankToNull` 之后可能是 null。列表/选人场景缺一两个字段不该整条崩掉。
 */
export const travelerSummarySchema = z.looseObject({
  /** 稳定对外句柄。 */
  travelerId: z.string().min(1),
  /** 中文姓名，展示唯一依据。 */
  name: z.string().nullable().default(null),
  /** 护照类证件下单用；⚠ 由录入方填，程序不得自行转拼音。 */
  surnamePinyin: z.string().nullable().default(null),
  givenNamePinyin: z.string().nullable().default(null),
  /** ADULT / CHILD / INFANT。 */
  kind: z.string().nullable().default(null),
  /** IC 身份证 / PP 护照 / GA 港澳…（开放集，不是枚举）。 */
  docType: z.string().nullable().default(null),
  /** ⚠ 仅展示，不可还原。**绝不能回传进 PUT**（会被 `rejectMaskedEcho` 拒）。 */
  docNumberMasked: z.string().nullable().default(null),
  /** yyyy-MM-dd。 */
  docValidTo: z.string().nullable().default(null),
  /** ⚠ 仅展示，不可还原。 */
  phoneMasked: z.string().nullable().default(null),
  /** 个人册 false / 企业共享册 true。⚠ 只在新建时可定，PUT 改不了。 */
  tenantShared: z.boolean().default(false),
})
export type TravelerSummary = z.infer<typeof travelerSummarySchema>

/** `GET /api/travelers` 直接回**裸数组**（不套 PageResult —— 契约驳回了分页，见下）。 */
export const travelerListSchema = z.array(travelerSummarySchema)

/**
 * 新建 / 编辑共用的请求体（`TravelerUpsertRequest`）。
 *
 * ★ 明文入口：`docNo` / `phone` 是全链路唯一的明文入口。
 *   前端务必：只走 HTTPS、**不落本地缓存、不进日志、不进埋点**。
 *
 * ⚠ 全部 optional：更新路径靠「留空 = 保持不变」，所以只发用户真改过的字段。
 *   新建路径该必填的由**服务端**校验（前端表单另做必填提示），schema 这层不强制 ——
 *   强制了反而挡住「只改手机号」这种合法的部分提交。
 */
export const travelerUpsertSchema = z.looseObject({
  name: z.string().max(100).optional(),
  surnamePinyin: z.string().max(64).optional(),
  givenNamePinyin: z.string().max(64).optional(),
  /** 留空则服务端按 birthday 推导。 */
  kind: z.string().max(8).optional(),
  docType: z.string().max(8).optional(),
  /** ★ 明文。⚠ 换证件类型（改 docType）时**必须同时给它**，否则 400。 */
  docNo: z.string().max(64).optional(),
  docValidTo: z.string().max(10).optional(),
  docIssueCountry: z.string().max(2).optional(),
  gender: z.string().max(1).optional(),
  nationality: z.string().max(2).optional(),
  birthday: z.string().max(10).optional(),
  /** ★ 明文，同 docNo。 */
  phone: z.string().max(32).optional(),
  /** 只在新建（POST）生效，PUT 忽略。 */
  tenantShared: z.boolean().optional(),
})
export type TravelerUpsertRequest = z.infer<typeof travelerUpsertSchema>
